// crewbit-admin/src/components/money/settings/PaidPanel.tsx
//
// Getting paid group sub-screen body. Read-only v1 per scope-down ack:
//   - Stripe Connect status row (live status from useBusinessFinancialSettings).
//   - Accepted methods row (LEARN — folded into world-class Stripe deep
//     integration).
//   - Late-fee policy row (LEARN — folded into post-launch session).
//
// NO inline editors v1. Payment terms / deposit % editor folded into
// world-class onboarding defaults (ships paired with onboarding bootstrap).
// Tips toggle folded into hosted-page tips selector ship.
//
// Mirrors crewbit-mobile/components/money/settings/GettingPaidPanel.tsx.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CreditCard, FileText, Clock } from 'lucide-react';
import { palette, radii, spacing, typography } from '../../../styles/tokens';
import { Skeleton } from '../../common/Skeleton';
import { Toast } from '../../common/Toast';
import { MoneyInput } from '../../common/MoneyInput';
import { canonicalMoney, parseMoney } from '../../../lib/money';
import { supabase } from '../../../lib/supabase';
import {
  useBusinessFinancialSettings,
  type StripeConnectStatus,
} from '../../../hooks/useBusinessFinancialSettings';
import { useEnableStripeFeatures } from '../../../hooks/useEnableStripeFeatures';
import {
  updateInvoiceSettings,
  type AcceptedMethod,
  type LateFeeType,
} from '../../../lib/invoiceSettingsMutations';
import { lookupErrorCopy } from '../../../lib/errorMap';
import SettingsStatusPill, { type SettingsStatusPillKind } from './SettingsStatusPill';
import TipsToggleRow, { type TipsToggleResult } from './TipsToggleRow';

interface InvoiceSettingsRow {
  accepted_methods: AcceptedMethod[];
  late_fee_type: LateFeeType;
  late_fee_amount: number;
}

const ALL_METHODS: { value: AcceptedMethod; label: string }[] = [
  { value: 'card', label: 'Card' },
  { value: 'etransfer', label: 'E-transfer' },
  { value: 'cash', label: 'Cash' },
];

const LATE_FEE_OPTIONS: { value: LateFeeType; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'percent', label: 'Percent' },
  { value: 'flat', label: 'Flat fee' },
];

interface StripeRowMapping {
  pillKind: SettingsStatusPillKind;
  pillLabel: string;
  body: string;
}

const STRIPE_MAP: Record<StripeConnectStatus, StripeRowMapping> = {
  active: {
    pillKind: 'active',
    pillLabel: 'Connected',
    body: 'Card payments are enabled. Customers can pay invoices online; payouts arrive on your Stripe schedule.',
  },
  charges_disabled: {
    pillKind: 'invite',
    pillLabel: 'Charges disabled',
    body: 'Stripe is connected but card charges are blocked. Open Stripe to resolve verification or compliance.',
  },
  payouts_disabled: {
    pillKind: 'invite',
    pillLabel: 'Payouts disabled',
    body: 'Stripe is connected and accepting charges, but payouts are paused. Open Stripe to resolve.',
  },
  not_connected: {
    pillKind: 'invite',
    pillLabel: 'Not connected',
    body: 'Connect Stripe to accept card and bank payments on invoices. Setup takes about 5 minutes.',
  },
};

interface PaidPanelProps {
  businessId: string | null;
}

interface ToastState {
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  isVisible: boolean;
}

export default function PaidPanel({ businessId }: PaidPanelProps) {
  const settings = useBusinessFinancialSettings(businessId);
  const enableStripeFeatures = useEnableStripeFeatures();
  const [toast, setToast] = useState<ToastState>({
    message: '',
    type: 'info',
    isVisible: false,
  });

  const [invoiceSettings, setInvoiceSettings] = useState<InvoiceSettingsRow | null>(null);
  const [lateFeeAmountStr, setLateFeeAmountStr] = useState(canonicalMoney(0));
  const [savingMethods, setSavingMethods] = useState(false);
  const [savingLateFee, setSavingLateFee] = useState(false);

  // Read invoice_settings on mount + when business changes. RLS allows
  // owners/managers; rendering surface this panel only on those roles.
  useEffect(() => {
    let cancelled = false;
    if (!businessId) return;
    void (async () => {
      const { data } = await supabase
        .from('invoice_settings')
        .select('accepted_methods, late_fee_type, late_fee_amount')
        .eq('business_id', businessId)
        .maybeSingle();
      if (cancelled) return;
      if (data) {
        const VALID_METHODS = ['card', 'etransfer', 'cash'] as const;
        const methods: AcceptedMethod[] = Array.isArray(data.accepted_methods)
          ? (data.accepted_methods as unknown[])
              .filter((m): m is AcceptedMethod =>
                typeof m === 'string' && (VALID_METHODS as readonly string[]).includes(m))
          : ['card', 'etransfer', 'cash'];
        const row: InvoiceSettingsRow = {
          accepted_methods: methods,
          late_fee_type: (data.late_fee_type as LateFeeType) ?? 'none',
          late_fee_amount: Number(data.late_fee_amount ?? 0),
        };
        setInvoiceSettings(row);
        setLateFeeAmountStr(canonicalMoney(row.late_fee_amount));
      } else {
        setInvoiceSettings({ accepted_methods: ['card', 'etransfer', 'cash'], late_fee_type: 'none', late_fee_amount: 0 });
        setLateFeeAmountStr(canonicalMoney(0));
      }
    })();
    return () => { cancelled = true; };
  }, [businessId]);

  const showToast = (message: string, type: ToastState['type']) =>
    setToast({ message, type, isVisible: true });

  const tipsEnabled = settings.data?.tipsEnabled ?? false;
  const status = settings.data?.stripeConnectStatus ?? 'not_connected';
  const tipsDisabled = status !== 'active';

  const handleMethodToggle = async (method: AcceptedMethod) => {
    if (!businessId || !invoiceSettings) return;
    const next = invoiceSettings.accepted_methods.includes(method)
      ? invoiceSettings.accepted_methods.filter((m) => m !== method)
      : [...invoiceSettings.accepted_methods, method];
    if (next.length === 0) {
      showToast('At least one payment method is required.', 'warning');
      return;
    }
    setSavingMethods(true);
    const result = await updateInvoiceSettings({ businessId, acceptedMethods: next });
    setSavingMethods(false);
    if (result.success) {
      setInvoiceSettings({ ...invoiceSettings, accepted_methods: next });
      showToast('Accepted methods updated.', 'success');
    } else {
      showToast(lookupErrorCopy('update_invoice_settings', result.error.code), 'error');
    }
  };

  const handleLateFeeTypeChange = async (next: LateFeeType) => {
    if (!businessId || !invoiceSettings) return;
    setSavingLateFee(true);
    const result = await updateInvoiceSettings({
      businessId,
      lateFeeType: next,
      lateFeeAmount: next === 'none' ? 0 : Number(parseMoney(lateFeeAmountStr)),
    });
    setSavingLateFee(false);
    if (result.success) {
      setInvoiceSettings({
        ...invoiceSettings,
        late_fee_type: next,
        late_fee_amount: next === 'none' ? 0 : Number(parseMoney(lateFeeAmountStr)),
      });
      if (next === 'none') setLateFeeAmountStr(canonicalMoney(0));
      showToast('Late-fee policy updated.', 'success');
    } else {
      showToast(lookupErrorCopy('update_invoice_settings', result.error.code), 'error');
    }
  };

  const handleLateFeeAmountBlur = async () => {
    if (!businessId || !invoiceSettings) return;
    const next = Number(parseMoney(lateFeeAmountStr));
    if (next === invoiceSettings.late_fee_amount) return;
    setSavingLateFee(true);
    const result = await updateInvoiceSettings({ businessId, lateFeeAmount: next });
    setSavingLateFee(false);
    if (result.success) {
      setInvoiceSettings({ ...invoiceSettings, late_fee_amount: next });
      showToast('Late-fee amount updated.', 'success');
    } else {
      showToast(lookupErrorCopy('update_invoice_settings', result.error.code), 'error');
    }
  };

  const handleTipsToggle = async (next: boolean): Promise<TipsToggleResult> => {
    if (!businessId) return { success: false };
    const result = await enableStripeFeatures({
      businessId,
      features: { tips_enabled: next },
    });
    if (result.success) {
      showToast(next ? 'Tips enabled' : 'Tips disabled', 'success');
      return { success: true };
    }
    const copy = lookupErrorCopy('enable_stripe_connect_features', result.error.code);
    showToast(copy, 'error');
    return { success: false, message: copy };
  };

  const handleDisabledTipsTap = (message: string) => {
    showToast(message, 'info');
  };

  if (settings.isLoading && !settings.data) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
        <Skeleton height={120} />
        <Skeleton height={100} />
        <Skeleton height={100} />
        <Skeleton height={100} />
      </div>
    );
  }

  const stripeCopy = STRIPE_MAP[status];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
      <Card>
        <Row icon={<CreditCard size={18} color={palette.accent} strokeWidth={2} />}>
          <TitleRow title="Stripe Connect" pillKind={stripeCopy.pillKind} pillLabel={stripeCopy.pillLabel} />
          <Body>{stripeCopy.body}</Body>
          {status === 'not_connected' ? (
            <Link
              to="/settings/payments"
              style={{
                fontSize: typography.size.caption,
                color: palette.accent,
                fontWeight: 600,
                textDecoration: 'none',
                marginTop: spacing.xs,
              }}
            >
              Set up Stripe Connect →
            </Link>
          ) : (
            <Link
              to="/settings/payments"
              style={{
                fontSize: typography.size.caption,
                color: palette.accent,
                fontWeight: 600,
                textDecoration: 'none',
                marginTop: spacing.xs,
              }}
            >
              Manage in Payments →
            </Link>
          )}
        </Row>
      </Card>

      <Card padding={0}>
        <TipsToggleRow
          label="Accept tips on hosted invoice page"
          description="Customers see a 15% / 20% / 25% / Custom tip selector when they pay online. Tips show separately in your reports."
          value={tipsEnabled}
          disabled={tipsDisabled}
          disabledMessage="Connect Stripe first to enable tips. Tips appear on the hosted invoice page customers see when they pay online."
          onToggle={handleTipsToggle}
          onDisabledTap={handleDisabledTipsTap}
        />
      </Card>

      <Card>
        <Row icon={<FileText size={18} color={palette.accent} strokeWidth={2} />}>
          <div style={{ fontSize: typography.size.subheading, fontWeight: 600, color: palette.g800 }}>
            Accepted methods
          </div>
          <Body>
            Choose which methods customers can use on the hosted invoice page.
          </Body>
          <div style={{ display: 'flex', gap: spacing.xs, flexWrap: 'wrap', marginTop: spacing.xs }}>
            {ALL_METHODS.map((m) => {
              const active = invoiceSettings?.accepted_methods.includes(m.value) ?? false;
              return (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => void handleMethodToggle(m.value)}
                  disabled={savingMethods || !invoiceSettings}
                  style={{
                    padding: '6px 14px',
                    borderRadius: 999,
                    border: `1px solid ${active ? palette.accent : palette.g200}`,
                    background: active ? palette.accentWash : palette.white,
                    color: active ? palette.accent : palette.g700,
                    fontSize: typography.size.caption,
                    fontWeight: active ? 600 : 500,
                    cursor: (savingMethods || !invoiceSettings) ? 'not-allowed' : 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  {m.label}
                </button>
              );
            })}
          </div>
        </Row>
      </Card>

      <Card>
        <Row icon={<Clock size={18} color={palette.accent} strokeWidth={2} />}>
          <div style={{ fontSize: typography.size.subheading, fontWeight: 600, color: palette.g800 }}>
            Late-fee policy
          </div>
          <Body>
            Apply a percent or flat fee to overdue invoices. None means no auto-applied fee.
          </Body>
          <div style={{ display: 'flex', gap: spacing.xs, marginTop: spacing.xs, flexWrap: 'wrap' }}>
            {LATE_FEE_OPTIONS.map((o) => {
              const active = invoiceSettings?.late_fee_type === o.value;
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => void handleLateFeeTypeChange(o.value)}
                  disabled={savingLateFee || !invoiceSettings}
                  style={{
                    padding: '6px 14px',
                    borderRadius: 999,
                    border: `1px solid ${active ? palette.accent : palette.g200}`,
                    background: active ? palette.accentWash : palette.white,
                    color: active ? palette.accent : palette.g700,
                    fontSize: typography.size.caption,
                    fontWeight: active ? 600 : 500,
                    cursor: (savingLateFee || !invoiceSettings) ? 'not-allowed' : 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
          {invoiceSettings && invoiceSettings.late_fee_type !== 'none' && (
            <div style={{ marginTop: spacing.sm, maxWidth: 200 }}>
              <div style={{ fontSize: typography.size.caption, color: palette.g700, marginBottom: spacing.xs }}>
                {invoiceSettings.late_fee_type === 'percent' ? 'Percent (%)' : 'Flat amount ($)'}
              </div>
              <MoneyInput
                value={lateFeeAmountStr}
                onChange={setLateFeeAmountStr}
                onBlur={() => void handleLateFeeAmountBlur()}
                ariaLabel="Late fee amount"
              />
            </div>
          )}
        </Row>
      </Card>

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onDismiss={() => setToast((t) => ({ ...t, isVisible: false }))}
      />
    </div>
  );
}

function Card({ children, padding }: { children: React.ReactNode; padding?: number }) {
  return (
    <div style={{
      background: palette.white,
      borderRadius: radii.lg,
      border: `1px solid ${palette.g150}`,
      padding: padding === undefined ? spacing.md : padding,
    }}>
      {children}
    </div>
  );
}

function Row({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: spacing.md, alignItems: 'flex-start' }}>
      <div style={{
        width: 36, height: 36, borderRadius: 18,
        background: palette.accentWash,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        {icon}
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: spacing.xs, minWidth: 0 }}>
        {children}
      </div>
    </div>
  );
}

function TitleRow({
  title,
  pillKind,
  pillLabel,
}: {
  title: string;
  pillKind: SettingsStatusPillKind;
  pillLabel: string;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, flexWrap: 'wrap' }}>
      <span style={{ fontSize: typography.size.subheading, fontWeight: 600, color: palette.g800 }}>
        {title}
      </span>
      <SettingsStatusPill kind={pillKind} label={pillLabel} />
    </div>
  );
}

function Body({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: typography.size.body,
      color: palette.g600,
      lineHeight: 1.5,
    }}>
      {children}
    </div>
  );
}
